import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

export const ToolButtonContext = React.createContext(false);

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  customBoop?: boolean;
  index: number;
  transformOrigin?: string;
  children: React.ReactNode;
}

function ToolButton({
  customBoop,
  index,
  transformOrigin,
  children,
  ...delegated
}: Props) {
  const [isBooped, setIsBooped] = React.useState(false);

  const wrapperSpring = useSpring({
    transform: customBoop ? `` : `rotate(${isBooped ? 10 : 0}deg)`,
    transformOrigin,
    config: {
      tension: 300,
      friction: 10,
    },
  });

  const timeoutId = React.useRef<number | null>(null);

  function handleTrigger() {
    setIsBooped(true);

    if (timeoutId.current) {
      window.clearTimeout(timeoutId.current);
    }

    timeoutId.current = window.setTimeout(() => {
      setIsBooped(false);
    }, 150);
  }

  return (
    <ToolButtonContext.Provider value={isBooped}>
      <Button
        data-block-grab-outline="true"
        onPointerDown={handleTrigger}
        onMouseEnter={handleTrigger}
        onFocus={handleTrigger}
        style={{
          '--index': index,
        }}
        {...delegated}
      >
        <IconWrapper style={wrapperSpring}>{children}</IconWrapper>
      </Button>
    </ToolButtonContext.Provider>
  );
}

const IconWrapper = styled(animated.span)`
  svg {
    display: block;
  }
`;

const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 8px;

  &:hover {
    background-color: var(--color-gray-100);
  }

  animation: slideIn 1000ms var(--spring) both;
  animation-delay: calc(var(--index) * 200ms + var(--enter-delay));

  @keyframes slideIn {
    from {
      transform: translateY(-50%);
      opacity: 0;
    }
  }
`;

export default ToolButton;
